import type { Model, ModelUpdateRequest } from '@bimdata/definitions/dto';
import api from './api';
import castDates from '@bimdata/client/utils/cast-dates';
import { ApiError } from '@bimdata/client';

async function fetch(id: string, sourceSize?: true, accessToken?: string): Promise<Model[]> {
  const { data } = await api.get<Model[]>(
    `/project/${id}/model`,
    {
      params: { ss: sourceSize ? 'yes' : undefined },
      accessToken
    }
  );
  return data.map((m) => castDates<Model>(m, [ 'loaded' ]));
}

async function remove(pid: string, modelId: number): Promise<void> {
  await api.delete(`/project/${pid}/model/${modelId}`);
}

async function cancelUpload(pid: string, uploadId: string): Promise<void> {
  await api.delete(
    `/project/${pid}/model/upload/${uploadId}`,
    {
      headers: {
        'tus-resumable': '1.0.0'
      }
    }
  );
}

async function reprocess(pid: string, ids: number[]): Promise<true | Record<number, Error>> {
  const { data } = await api.post<true | Record<number, Error>>(
    `/project/${pid}/model/reprocess`,
    { ids }
  );
  return data;
}

async function edit(pid: string, modelId: number, body: ModelUpdateRequest): Promise<Model> {
  const { data } = await api.put(`/project/${pid}/model/${modelId}`, body);
  return castDates<Model>(data, [ 'loaded' ]);
}

async function get(pid: string, modelId: number): Promise<Model> {
  const { data } = await api.get<Model>(`/project/${pid}/model/${modelId}`);
  return castDates<Model>(data, [ 'loaded' ]);
}

async function download(pid: string, modelId: number, onProgress?: (p: number, l: number, t?: number) => void, signal?: AbortSignal): Promise<File> {
  const response = await api.get(
    `/project/${pid}/model/${modelId}/download`,
    {
      responseType: 'blob',
      onDownloadProgress: onProgress && ((e) => onProgress(Math.round((e.progress || 0) * 100), e.loaded, e.total)),
      ...(signal ? { signal } : {})
    }
  );

  const parsedDisposition = (/filename="([^"]+)"/i).exec(response.headers['content-disposition'] || '');
  const fn = parsedDisposition ? decodeURIComponent(parsedDisposition[1]) : 'data';
  return new File([(response.data as Blob)], fn);
}

export {
  fetch,
  remove,
  cancelUpload,
  edit,
  get,
  download,
  reprocess
};